// src/components/CreateEntryModal.jsx
import React, { useEffect, useMemo, useRef, useState, } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Box,
} from "@mui/material";
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs from 'dayjs';
import {
  isPermissionDenied,
  getPermissionDeniedMessage,
  getWriteErrorMessage,
  isVersionConflict,
  shouldReloadAfterWriteError,
  loadedModifiedDate,
  MODIFIED_DATE_FIELD,
} from "@1823-partners/core";
import { saveEntry, fetchCounterparties } from "../api";
import { formatEntryData, validateEntryData, isPlaceholderEntryId, newEntryId } from "../utils/entryUtils";
import config from "../data/config.json";
import { getOwnerFieldOptions } from '../utils/ownerChoices';

// The writer stamps these; they are never user input.
const WRITER_STAMPED = [MODIFIED_DATE_FIELD, 'MODIFIED_BY'];

const CreateEntryModal = ({ open, onClose, onCreated, onWriteConflict, reserveEntryId, userId, initialData }) => {
  const [counterparties, setCounterparties] = useState([]);
  const [loadingCounterparties, setLoadingCounterparties] = useState(false);
  const [validationErrors, setValidationErrors] = useState([]);

  // Fetch counterparties on component mount
  useEffect(() => {
    const loadCounterparties = async () => {
      setLoadingCounterparties(true);
      try {
        const response = await fetchCounterparties();
        // Extract only the COUNTERPARTY field from each object
        const counterpartyList = (response.data || []).map(item => item.COUNTERPARTY).filter(cp => cp);
        setCounterparties(counterpartyList);
      } catch (error) {
        console.error('Error fetching counterparties:', error);
        if (isPermissionDenied(error)) {
          alert(getPermissionDeniedMessage(error));
        }
        // Fallback to empty array if API fails
        setCounterparties([]);
      } finally {
        setLoadingCounterparties(false);
      }
    };

    if (open) {
      loadCounterparties();
    }
  }, [open]);

  const defaultForm = useMemo(() => {
    return {
      DIRECTION: "",
      DATE: "",
      CUSIP: "",
      CURRENCY: "",
      SEC_OWNER: "",
      SEC_OWNER_TEAM: "",
      TYPE: "",
      COUNTERPARTY: "",
      CONNECTED_ENTITY: "",
      ILLIQUID: "",
      FLOATER: "",
      DESCRIPTION: "",
      ORIGINAL_FACE: "",
      CURRENT_FACE: "",
      PRICE: "",
      EST_PROCEEDS: "",
      MARKET_SPREAD: "",
      TREAS_SPREAD: "",
      BASE_YIELD: "",
      WAL: "",
      DURATION: "",
      OVERRIDE: "",
      RATING: "",
      CURR_CREDIT_SUPPORT: "",
      SSFA_DETACHMENT_POINT: "",
      THICKNESS: "",
      ACCOUNT: "",
      NOTES: "",
      MODIFIED_BY: userId,
      // Never saved: the writer accepts a null token only for a new key.
      MODIFIED_DATE: null,
      BLOTTER_ID: ""
    };
  }, [userId]);

  const [formData, setFormData] = useState(initialData || defaultForm);
  const [loading, setLoading] = useState(false);
  // The BLOTTER_ID a new entry's save sends: picked on the first submit and
  // kept across retries, so a retry whose first attempt landed is a 409 at
  // the writer instead of a second copy of the entry.
  const newEntryIdRef = useRef(null);
  // Conflict handling for an edit, as a small state machine:
  //
  // - baseRowRef: the stored row the form is based on. initialData until a
  //   409, then the fresh row the grid reloaded. A submit sends its
  //   MODIFIED_DATE.
  // - userEditsRef: field -> the value the user set, maintained only by the
  //   form's change handlers. Setting a field back to the base row's value
  //   removes it (a revert is not an edit). A rebase never re-derives it, so
  //   a field a fresh row happens to match stays the user's across any
  //   number of 409s.
  // - On a 409 the form becomes { ...freshRow, ...userEdits } (built from the
  //   edits as they are when the reload resolves, so typing during the reload
  //   is kept) and the base moves to the fresh row. The user reviews it and
  //   submits again deliberately.
  // Both reset with the form (a new initialData, or a successful save).
  const baseRowRef = useRef(null);
  const userEditsRef = useRef({});

  // When initialData changes (i.e. when editing an existing entry), update the form.
  useEffect(() => {
    newEntryIdRef.current = null;
    baseRowRef.current = null;
    userEditsRef.current = {};
    if (initialData) {
      // Keep the date in the original format for the DateField
      setFormData({
        ...initialData,
        DATE: initialData.DATE || ""
      });
    } else {
      setFormData(defaultForm);
    }
  }, [initialData, defaultForm]);

  // Clear validation errors when form data changes
  useEffect(() => {
    if (validationErrors.length > 0) {
      setValidationErrors([]);
    }
  }, [formData, validationErrors.length]);

  const validateForm = () => {
    return validateEntryData(formData, !initialData);
  };

  const recordEdit = (name, value) => {
    const base = baseRowRef.current || initialData || defaultForm;
    if (String(value ?? '') === String(base?.[name] ?? '')) {
      delete userEditsRef.current[name];
    } else {
      userEditsRef.current[name] = value;
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    const next = type === "checkbox" ? checked : value;
    recordEdit(name, next);
    setFormData((prev) => ({ ...prev, [name]: next }));
  };

  const handleDateChange = (name, dayjsValue) => {
    // Convert dayjs to ISO string format for storage, or null if invalid
    const dateValue = dayjsValue && dayjsValue.isValid() ? dayjsValue.format('YYYY-MM-DD') : '';
    recordEdit(name, dateValue);
    setFormData((prev) => ({ ...prev, [name]: dateValue }));
  };


  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate required fields
    const emptyFields = validateForm();

    if (emptyFields.length > 0) {
      setValidationErrors(emptyFields);
      return;
    }

    setLoading(true);
    // An edit sends the MODIFIED_DATE the grid loaded for this entry, unchanged
    // (the form never edits it); a new entry sends its stable id and null.
    const originalId = formData.BLOTTER_ID;
    const isNewEntry = isPlaceholderEntryId(originalId);
    let entryId = originalId;
    if (isNewEntry) {
      // A grid placeholder shares the grid's reserved id; a brand-new form
      // keeps its own across retries.
      if (originalId && reserveEntryId) {
        entryId = reserveEntryId(originalId);
      } else {
        if (!newEntryIdRef.current) newEntryIdRef.current = newEntryId();
        entryId = newEntryIdRef.current;
      }
    }
    const base = baseRowRef.current || initialData;
    const token = !isNewEntry && base ? loadedModifiedDate(base) : null;
    const dataToSubmit = {
      ...formatEntryData(formData),
      BLOTTER_ID: entryId,
      [MODIFIED_DATE_FIELD]: token,
    };

    try {
      await saveEntry(dataToSubmit);
      // Tell the caller which placeholder (if any) just got persisted so it
      // can drop the stale `new_row_*` row — the backend rewrote its ID, so
      // the refreshed server data won't match it on BLOTTER_ID.
      const savedPlaceholderId =
        typeof originalId === 'string' && originalId.startsWith('new_row_')
          ? originalId
          : null;
      newEntryIdRef.current = null;
      baseRowRef.current = null;
      userEditsRef.current = {};
      onCreated && onCreated(savedPlaceholderId);
      onClose();
      setFormData(defaultForm);
      setValidationErrors([]);
    } catch (error) {
      console.error("Error creating/updating entry", error);
      if (isPermissionDenied(error)) {
        alert(getPermissionDeniedMessage(error));
      } else {
        // The backend's own text: a conflict, a bad request or an unknown outcome.
        alert(getWriteErrorMessage(error, "Error creating/updating entry"));
      }
      if (shouldReloadAfterWriteError(error) && onWriteConflict) {
        // The grid behind the form is stale (or the save may have landed):
        // reload it. The form keeps the user's input.
        const freshRow = await onWriteConflict(originalId);
        if (isVersionConflict(error) && !isNewEntry && freshRow && base) {
          // Rebase: the other user's saved values, with only the user's own
          // edits on top, so a re-submit never sends a stale copy of fields
          // they did not touch. Read the edits now, not from this submit's
          // closure: the form stays editable while the reload runs.
          baseRowRef.current = freshRow;
          const edits = Object.fromEntries(
            Object.entries(userEditsRef.current).filter(([field]) => !WRITER_STAMPED.includes(field))
          );
          setFormData({ ...freshRow, ...edits });
        }
      }
    }
    setLoading(false);
  };

  const fields = useMemo(() => [
    { name: "DIRECTION", type: "select", options: config.directions },
    { name: "DATE", type: "date" },
    { name: "CUSIP", type: "text" },
    { name: "CURRENCY", type: "select", options: config.currencies },
    { name: "SEC_OWNER", type: "select", options: config.securityOwners },
    { name: "SEC_OWNER_TEAM", type: "select", options: config.securityOwnerTeams },
    { name: "TYPE", type: "select", options: config.securityTypes },
    { name: "COUNTERPARTY", type: "select", options: counterparties, loading: loadingCounterparties },
    { name: "CONNECTED_ENTITY", type: "select", options: config.connectedEntities },
    { name: "ILLIQUID", type: "select", options: config.booleanOptions },
    { name: "FLOATER", type: "select", options: config.booleanOptions },
    { name: "DESCRIPTION", type: "text" },
    { name: "ORIGINAL_FACE", type: "number" },
    { name: "CURRENT_FACE", type: "number" },
    { name: "PRICE", type: "number" },
    { name: "MARKET_SPREAD", type: "number" },
    { name: "TREAS_SPREAD", type: "number" },
    { name: "BASE_YIELD", type: "number" },
    { name: "WAL", type: "number" },
    { name: "DURATION", type: "number" },
    { name: "OVERRIDE", type: "select", options: config.booleanOptions },
    { name: "RATING", type: "select", options: config.ratings },
    { name: "CURR_CREDIT_SUPPORT", type: "number" },
    { name: "SSFA_DETACHMENT_POINT", type: "number" },
    { name: "ACCOUNT", type: "select", options: config.accounts },
    { name: "NOTES", type: "text" }
  ], [counterparties, loadingCounterparties]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xl"
      fullWidth
    >
      <DialogTitle>
        {initialData ? "Edit Blotter Entry" : "Create New Blotter Entry"}
      </DialogTitle>
      <form onSubmit={handleSubmit}>
        <DialogContent dividers sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 2 }}>
          {validationErrors.length > 0 && (
            <Box sx={{ gridColumn: '1 / -1', mb: 2 }}>
              <Alert severity="error">
                <strong>Missing required fields:</strong> {validationErrors.map(field => field.replace(/_/g, ' ')).join(', ')}
              </Alert>
            </Box>
          )}
          {fields.map((field) => {
            const isRequired = config.validation.requiredFields.includes(field.name);
            const options = ['SEC_OWNER', 'SEC_OWNER_TEAM'].includes(field.name)
              ? getOwnerFieldOptions(field.name, formData[field.name])
              : field.options;
            return (
              <FormControl fullWidth margin="normal" key={field.name}>
                {field.type === "select" ? ([
                  <InputLabel key={`${field.name}_inputlabel`}>{field.name.replace(/_/g, ' ') + (isRequired ? "*" : "")}</InputLabel>,
                  <Select
                    key={`${field.name}_select`}
                    label={field.name}
                    name={field.name}
                    onChange={handleChange}
                    required={isRequired}
                    value={formData[field.name] || ""}
                    disabled={field.loading}
                    error={validationErrors.includes(field.name)}
                  >
                    {field.loading ? (
                      <MenuItem disabled>Loading...</MenuItem>
                    ) : (
                      options?.map((option) => (
                        <MenuItem key={option} value={option}>{option}</MenuItem>
                      ))
                    )}
                  </Select>
                ]) : field.type === "date" ? (
                  <DatePicker
                    key={`${field.name}_datepicker`}
                    label={field.name.replace(/_/g, ' ')}
                    value={formData[field.name] ? dayjs(formData[field.name]) : null}
                    onChange={(dayjsValue) => handleDateChange(field.name, dayjsValue)}
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        margin: "normal",
                        required: isRequired,
                        error: validationErrors.includes(field.name)
                      }
                    }}
                  />
                ) : field.type === "number" ? (
                  <TextField
                    fullWidth
                    label={field.name.replace(/_/g, ' ')}
                    margin="normal"
                    name={field.name}
                    onChange={handleChange}
                    required={isRequired}
                    type="number"
                    value={formData[field.name]}
                    error={validationErrors.includes(field.name)}
                  />
                ) : (
                  <TextField
                    fullWidth
                    label={field.name.replace(/_/g, ' ')}
                    margin="normal"
                    name={field.name}
                    onChange={handleChange}
                    required={isRequired}
                    value={formData[field.name]}
                    error={validationErrors.includes(field.name)}
                  />
                )}
              </FormControl>
            );
          })}
          <TextField
            fullWidth
            margin="normal"
            label="Modified By"
            name="MODIFIED_BY"
            value={formData.MODIFIED_BY || userId}
            disabled
          />
          <TextField
            fullWidth
            margin="normal"
            label="Blotter ID"
            name="BLOTTER_ID"
            value={formData.BLOTTER_ID || ""}
            disabled
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} disabled={loading} sx={{ color: "black" }}>
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={loading}
            sx={{ backgroundColor: "lightgrey", color: "black" }}
          >
            {loading
              ? initialData
                ? "Updating..."
                : "Creating..."
              : initialData
                ? "Update Entry"
                : "Create Entry"}
          </Button>
        </DialogActions>
      </form>
    </Dialog >
  );
};

export default CreateEntryModal;
